import React, { useMemo, useState } from 'react';
import { MemberRegistrationForm, MembershipType, FamilyMember, Gender, ShirtSize } from '../../types';
import { isUnderAge } from '../../utils/membership';
import { toTitleCase } from '../../utils/textNormalization';
import DateOfBirthSelect from '../ui/DateOfBirthSelect';
import { describeFamilyPrice, FAMILY_MAX_ADULTS, FAMILY_MAX_SIZE } from '@shared/fees';
import { householdFromMembers } from '@shared/familyLimits';
import { clubYear } from '@shared/membership';

interface FamilyMembersStepProps {
  formData: MemberRegistrationForm;
  updateFormData: (updates: Partial<MemberRegistrationForm>) => void;
  onNext: () => void;
  onBack: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FamilyMembersStep: React.FC<FamilyMembersStepProps> = ({
  formData,
  updateFormData,
  onNext,
  onBack,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isSponsor = formData.membershipType !== MembershipType.FAMILY;
  const year = clubYear();

  // Adult/junior is worked out from the date of birth with the club age rule
  // (#127, D7) — there is no manual Adult/Child choice any more. A member with
  // no date of birth entered yet counts as neither for the live counters below
  // (they're blocked from continuing by validateForm's "date of birth is
  // required" check), but is still included in the person/size count.
  const peopleCount = formData.familyMembers.length;
  const adultCount = formData.familyMembers.filter(
    (m) => m.dateOfBirth && !isUnderAge(m.dateOfBirth, new Date())
  ).length;
  // Primary registrant counts as the first adult (Q1: the primary must be an adult).
  const totalAdults = adultCount + 1;
  const totalPeople = peopleCount + 1;

  const household = useMemo(
    () =>
      householdFromMembers(
        [
          { dateOfBirth: formData.dateOfBirth, isPrimary: true },
          ...formData.familyMembers.map((m) => ({ dateOfBirth: m.dateOfBirth })),
        ],
        year
      ),
    [formData.dateOfBirth, formData.familyMembers, year]
  );
  const priceLine = household.adults <= FAMILY_MAX_ADULTS ? describeFamilyPrice(household) : null;

  const canAddPerson = totalPeople < FAMILY_MAX_SIZE;

  const addFamilyMember = () => {
    if (!canAddPerson) return;
    const newMember: FamilyMember = {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: Gender.PREFER_NOT_TO_SAY,
      shirtSize: ShirtSize.M,
      // Kept for backwards compatibility with the server, which ignores this
      // field and derives adult/junior from the date of birth itself. Set
      // from the DOB once one is entered (updateFamilyMember below).
      isAdult: true,
    };
    updateFormData({ familyMembers: [...formData.familyMembers, newMember] });
  };

  const removeFamilyMember = (index: number) => {
    const updated = formData.familyMembers.filter((_, i) => i !== index);
    updateFormData({ familyMembers: updated });
  };

  const updateFamilyMember = (index: number, updates: Partial<FamilyMember>) => {
    const updated = formData.familyMembers.map((member, i) => {
      if (i !== index) return member;
      const merged = { ...member, ...updates };
      // Re-derive isAdult from the date of birth every time it (or anything
      // else) changes, so it can never go stale against what's typed — kept
      // only for backwards compatibility; the server ignores it (#127, D7).
      if (merged.dateOfBirth) {
        merged.isAdult = !isUnderAge(merged.dateOfBirth, new Date());
      }
      return merged;
    });
    updateFormData({ familyMembers: updated });
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (totalAdults > FAMILY_MAX_ADULTS) {
      newErrors.general =
        'A family can have at most 2 adults. A third adult can take an individual membership.';
    } else if (totalPeople > FAMILY_MAX_SIZE) {
      newErrors.general = `A family can have at most ${FAMILY_MAX_SIZE} people. Please contact the club about larger families.`;
    }

    formData.familyMembers.forEach((member, index) => {
      if (!member.firstName.trim()) newErrors[`firstName_${index}`] = 'First name is required';
      if (!member.lastName.trim()) newErrors[`lastName_${index}`] = 'Last name is required';
      if (!member.dateOfBirth) {
        newErrors[`dateOfBirth_${index}`] = 'Date of birth is required';
      }

      // Email required only when needsPortalAccess is true
      if (member.needsPortalAccess) {
        if (!member.email?.trim()) {
          newErrors[`email_${index}`] = 'Email is required for portal access';
        } else if (!EMAIL_REGEX.test(member.email)) {
          newErrors[`email_${index}`] = 'Please enter a valid email address';
        }
      } else if (member.email && !EMAIL_REGEX.test(member.email)) {
        newErrors[`email_${index}`] = 'Please enter a valid email address';
      }
    });

    // Duplicate-email check across all supplied emails and the primary's email
    const allEmails = formData.familyMembers
      .map((m, i) => ({ email: m.email?.trim().toLowerCase(), index: i }))
      .filter((e) => e.email);
    allEmails.forEach(({ email, index }) => {
      if (email === formData.email?.trim().toLowerCase()) {
        newErrors[`email_${index}`] = 'This email is already used by the primary registrant';
      } else if (allEmails.filter((e) => e.email === email).length > 1) {
        newErrors[`email_${index}`] = 'Each family member must have a unique email address';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) onNext();
  };

  const renderMemberCard = (member: FamilyMember, index: number) => {
    const roleTag = member.dateOfBirth
      ? isUnderAge(member.dateOfBirth, new Date())
        ? 'Junior'
        : 'Adult'
      : null;

    return (
      <div key={index} className="border border-gray-300 rounded-lg p-6 relative">
        <button
          onClick={() => removeFamilyMember(index)}
          className="absolute top-4 right-4 text-red-500 hover:text-red-700 text-sm min-h-[44px] min-w-[44px]"
        >
          ✕ Remove
        </button>
        <h3 className="text-lg font-semibold text-gray-800 mb-1 flex items-center gap-2">
          Family Member
          {roleTag && (
            <span
              aria-label={`${roleTag} (worked out from date of birth)`}
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                roleTag === 'Adult' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
              }`}
            >
              {roleTag}
            </span>
          )}
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Adult or junior is worked out from the date of birth (16 or under on 1 January of the
          membership year is a junior for the whole year).
        </p>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor={`family-firstname-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                id={`family-firstname-${index}`}
                type="text"
                value={member.firstName}
                onChange={(e) => updateFamilyMember(index, { firstName: toTitleCase(e.target.value) })}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent ${
                  errors[`firstName_${index}`] ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="First name"
              />
              {errors[`firstName_${index}`] && <p className="text-red-500 text-sm mt-1">{errors[`firstName_${index}`]}</p>}
            </div>
            <div>
              <label htmlFor={`family-lastname-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                id={`family-lastname-${index}`}
                type="text"
                value={member.lastName}
                onChange={(e) => updateFamilyMember(index, { lastName: toTitleCase(e.target.value) })}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent ${
                  errors[`lastName_${index}`] ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Last name"
              />
              {errors[`lastName_${index}`] && <p className="text-red-500 text-sm mt-1">{errors[`lastName_${index}`]}</p>}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
              <DateOfBirthSelect
                value={member.dateOfBirth}
                onChange={(v) => updateFamilyMember(index, { dateOfBirth: v })}
                error={!!errors[`dateOfBirth_${index}`]}
                idPrefix={`family-dob-${index}`}
              />
              {errors[`dateOfBirth_${index}`] && <p className="text-red-500 text-sm mt-1">{errors[`dateOfBirth_${index}`]}</p>}
            </div>
            <div>
              <label htmlFor={`family-gender-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                Gender *
              </label>
              <select
                id={`family-gender-${index}`}
                value={member.gender}
                onChange={(e) => updateFamilyMember(index, { gender: e.target.value as Gender })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent"
              >
                <option value={Gender.MALE}>Male</option>
                <option value={Gender.FEMALE}>Female</option>
                <option value={Gender.OTHER}>Other</option>
                <option value={Gender.PREFER_NOT_TO_SAY}>Prefer not to say</option>
              </select>
            </div>
            <div>
              <label htmlFor={`family-shirt-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                Shirt Size
              </label>
              <select
                id={`family-shirt-${index}`}
                value={member.shirtSize}
                onChange={(e) => updateFamilyMember(index, { shirtSize: e.target.value as ShirtSize })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent"
              >
                {Object.values(ShirtSize).map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                For club merchandise, when available. Kept on file for future merchandise runs only.
              </p>
            </div>
          </div>

          {/* Email / portal access — two age bands by the junior/adult rule (#127, D7): adult default on, junior default off */}
          {member.dateOfBirth && (() => {
            const isJunior = isUnderAge(member.dateOfBirth, new Date());
            if (!isJunior) {
              // Adult: toggle default ON
              const isOn = member.needsPortalAccess !== false;
              return (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 min-h-[44px]">
                    <input
                      type="checkbox"
                      checked={isOn}
                      onChange={(e) => updateFamilyMember(index, { needsPortalAccess: e.target.checked, email: e.target.checked ? member.email : '' })}
                      className="rounded border-gray-300 text-fishing-blue focus:ring-fishing-blue"
                    />
                    Give this person their own login (recommended)
                  </label>
                  {isOn && (
                    <div>
                      <label htmlFor={`family-email-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address *
                      </label>
                      <input
                        id={`family-email-${index}`}
                        type="email"
                        value={member.email || ''}
                        onChange={(e) => updateFamilyMember(index, { email: e.target.value })}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent ${errors[`email_${index}`] ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Email address for portal access"
                      />
                      {errors[`email_${index}`] && <p className="text-red-500 text-sm mt-1">{errors[`email_${index}`]}</p>}
                    </div>
                  )}
                </div>
              );
            }
            // Junior: toggle shown, default OFF
            return (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 min-h-[44px]">
                  <input
                    type="checkbox"
                    checked={member.needsPortalAccess || false}
                    onChange={(e) => updateFamilyMember(index, { needsPortalAccess: e.target.checked, email: e.target.checked ? member.email : '' })}
                    className="rounded border-gray-300 text-fishing-blue focus:ring-fishing-blue"
                  />
                  Give this person their own login
                </label>
                {member.needsPortalAccess && (
                  <div>
                    <label htmlFor={`family-email-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      id={`family-email-${index}`}
                      type="email"
                      value={member.email || ''}
                      onChange={(e) => updateFamilyMember(index, { email: e.target.value })}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent ${errors[`email_${index}`] ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Email address for portal access"
                    />
                    {errors[`email_${index}`] && <p className="text-red-500 text-sm mt-1">{errors[`email_${index}`]}</p>}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Family Members</h2>
      <p className="text-gray-600 mb-4">
        {formData.firstName} {formData.lastName} is registered as the primary adult. Add up to 1
        more adult and up to 9 juniors (16 or under on 1 January of the membership year — they
        stay junior for the whole year), up to {FAMILY_MAX_SIZE} people in total.
      </p>

      {/* Counters */}
      <div className="flex flex-wrap gap-4 mb-2" aria-live="polite">
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-sm">
          👤 Adults: <span className="font-semibold">{totalAdults}/{FAMILY_MAX_ADULTS}</span>
          <span className="text-xs text-gray-500 ml-1">(incl. you)</span>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm">
          👥 People: <span className="font-semibold">{totalPeople}/{FAMILY_MAX_SIZE}</span>
          <span className="text-xs text-gray-500 ml-1">(incl. you)</span>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Up to 2 adults and 3 juniors for $120 or less. $25 for each extra junior.
      </p>

      {!isSponsor && priceLine && (
        <div
          className="mb-6 p-3 bg-fishing-green/10 border border-fishing-green/30 rounded-lg text-sm font-medium text-fishing-green"
          aria-live="polite"
        >
          Family price: {priceLine}
        </div>
      )}

      {errors.general && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" role="alert">
          {errors.general}
        </div>
      )}

      {formData.familyMembers.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-600 mb-4">No additional family members added yet</p>
          <button
            onClick={addFamilyMember}
            disabled={!canAddPerson}
            className="px-5 py-2 min-h-[44px] bg-fishing-blue hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
          >
            + Add family member
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {formData.familyMembers.map((member, index) => renderMemberCard(member, index))}

          {canAddPerson ? (
            <button
              onClick={addFamilyMember}
              className="w-full py-3 min-h-[44px] border-2 border-dashed border-blue-300 rounded-lg text-blue-600 hover:border-fishing-blue hover:text-fishing-blue transition-colors"
            >
              + Add family member ({FAMILY_MAX_SIZE - totalPeople} remaining)
            </button>
          ) : (
            <div className="w-full py-3 min-h-[44px] border-2 border-dashed border-gray-200 rounded-lg text-gray-400 text-center flex items-center justify-center">
              Maximum {FAMILY_MAX_SIZE} people reached
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          className="px-6 py-2 min-h-[44px] border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          className="px-6 py-2 min-h-[44px] bg-fishing-blue hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default FamilyMembersStep;
